/** Números de dano flutuantes: elementos DOM projetados do 3D, reciclados via pool. */
import * as THREE from "three";
import { events } from "../core/Events";
import { i18n } from "../i18n/i18n";
import { Pool } from "../utils/Pool";
import { fmtNum } from "../utils/format";

interface Floating {
  el: HTMLDivElement;
  pos: THREE.Vector3;
  vy: number;
  life: number;
  maxLife: number;
}

export class DamageNumbers {
  private readonly layer: HTMLDivElement;
  private readonly pool: Pool<HTMLDivElement>;
  private readonly active: Floating[] = [];
  private readonly tmp = new THREE.Vector3();

  constructor(uiRoot: HTMLElement, private readonly camera: THREE.Camera) {
    this.layer = document.createElement("div");
    this.layer.className = "dmg-layer";
    uiRoot.appendChild(this.layer);

    this.pool = new Pool<HTMLDivElement>(
      () => {
        const d = document.createElement("div");
        d.className = "dmg-number";
        d.style.display = "none";
        // 10.9HD — o texto vive num <span>: o "pop" (scale) anima o span, não o div posicionado. A
        // propriedade `scale` no div era aplicada ANTES do transform de posição → o número nascia
        // deslocado (1,35× a posição) e "vinha da lateral até o monstro" (bug relatado pelo Thiago).
        d.appendChild(document.createElement("span"));
        this.layer.appendChild(d);
        return d;
      },
      (d) => {
        d.style.display = "none";
      },
      12,
    );

    events.on("damage:dealt", (p) => this.spawn(p.amount, p.crit, p.position, p.target, p.blocked === true, p.weak === true, p.resisted === true));
  }

  /**
   * 10.6G — GANHO FLUTUANTE no abate ("+4 ❤"). O jogo já dava vida e poder permanentes a
   * cada monstro morto (Absorção), mas nada aparecia na tela: a recompensa mais frequente
   * do jogo era invisível. Sobe do corpo do monstro, como na referência.
   */
  gain(text: string, worldPos: THREE.Vector3, kind: "hp" | "power" = "hp"): void {
    if (this.active.length >= 10) {
      const old = this.active.shift();
      if (old) this.pool.release(old.el);
    }
    const el = this.pool.acquire();
    (el.firstElementChild as HTMLElement).textContent = text;
    el.className = `dmg-number gain ${kind}`;
    el.style.display = "block";
    el.style.opacity = "1";
    const p = worldPos.clone();
    p.y += 1.7;
    p.x += (Math.random() - 0.5) * 0.6;
    this.active.push({ el, pos: p, vy: 1.5, life: 0, maxLife: 1.1 });
  }

  private spawn(amount: number, crit: boolean, worldPos: THREE.Vector3, target: "player" | "enemy", blocked = false, weak = false, resisted = false): void {
    // 10.5N — legibilidade: com a corrente + 4 armas os números empilhavam e viravam borrão.
    // Teto de números na tela (recicla o mais antigo) e espalhamento maior/escalonado.
    if (this.active.length >= 10) {
      const old = this.active.shift();
      if (old) this.pool.release(old.el);
    }
    const el = this.pool.acquire();
    // crítico não escreve mais a palavra "CRÍTICO" (ocupava 3x a largura e cobria os outros):
    // agora é só o número, maior e dourado. Bloqueio continua com rótulo (é informação nova).
    // 10.6G — o número ENSINA a fraqueza: acerto com a arma certa vem verde e marcado,
    // acerto com a arma que o bicho aguenta vem apagado e marcado. É a forma mais direta
    // de o jogador descobrir sozinho qual arma levar para cada território.
    const n = fmtNum(amount);
    (el.firstElementChild as HTMLElement).textContent = blocked
      ? `${i18n.t("combat.blocked")} ${n}`
      : weak
        ? `${n} ${i18n.t("combat.weak")}`
        : resisted
          ? `${n} ${i18n.t("combat.resisted")}`
          : `${n}`;
    el.className = `dmg-number ${blocked ? "blocked" : weak ? "weak" : resisted ? "resisted" : crit ? "crit" : target === "player" ? "player" : "normal"}`;
    el.style.display = "block";
    el.style.opacity = "1";
    // 10.9GS — reinicia a animação de "pop" (o elemento vem do pool; só trocar a classe não reinicia)
    const span = el.firstElementChild as HTMLElement;
    span.style.animation = "none";
    void span.offsetWidth;
    span.style.animation = "";

    const stagger = this.active.length % 4;
    worldPos.y += 1.25 + stagger * 0.32;
    worldPos.x += (Math.random() - 0.5) * 1.5;
    worldPos.z += (Math.random() - 0.5) * 0.9;
    this.active.push({ el, pos: worldPos, vy: 1.7 + stagger * 0.15, life: 0, maxLife: crit ? 1.0 : 0.8 });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const n = this.active[i];
      n.life += dt;
      const t = n.life / n.maxLife;
      if (t >= 1) {
        this.pool.release(n.el);
        this.active.splice(i, 1);
        continue;
      }
      n.pos.y += n.vy * dt;
      n.vy -= 2.4 * dt;

      this.tmp.copy(n.pos).project(this.camera);
      const x = (this.tmp.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-this.tmp.y * 0.5 + 0.5) * window.innerHeight;
      n.el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      n.el.style.opacity = String(t < 0.5 ? 1 : 1 - (t - 0.5) * 2);
    }
  }
}
